import _debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { sharedTableStyles } from '../../../../utils/tableStyles';
import { TableSkeleton } from '../../../../components/UI/Skeleton/Skeleton';
import TableCard from '../../../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../../../components/UI/TableToolbar/TableToolbar';
import RowActionMenu from '../../../../components/UI/RowActionMenu/RowActionMenu';
import { useDispatch } from 'react-redux';
import DataTable from 'react-data-table-component';

import { selectedKpi } from '../../../../redux/Slice/kpiSlice';
import { useDeleteKPIMutation, useGetKPIQuery } from '../../../../redux/apis/KPIApi';

import EmptyData from '../../../../components/UI/EmptyData';
import { DepartmentsData } from '../../../../components/Data/AppData';
import DeleteModel from '../../../../components/common/DeleteModel';

import {
  LB_HEADER,
  LB_KPI_MODULE,
  LB_KPI_TABLE_COLS,
  LB_LEAVE_TYPE_TABLE_COL_SORT
} from '../../../../utils/constant/labels';
import { BT_BUTTON_LABELS } from '../../../../utils/constant/buttons';
import { GLOBAL } from '../../../../utils/constant/global';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';
import { paginationRow, skipRow } from '../../../../utils/handler';
import { INPUT_LABEL } from '../../../../utils/constant/placeHolders';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const { TOAST_ERROR } = TOAST_TYPE;
const { DESC, ASC } = GLOBAL;
const { BTN_ADD_NEW_KPI } = BT_BUTTON_LABELS;
const { LB_KPI_TITLE } = LB_KPI_MODULE;
const { LB_EDIT_KPI } = LB_HEADER;
const { SR_LEAVE_TYPE } = LB_LEAVE_TYPE_TABLE_COL_SORT;
const { LB_DELETE_KPI, LB_DELETE_KPI_MESSAGE } = INPUT_LABEL;
const {
  LB_tBL_COL_NAME,
  LB_TBL_COL_DESIGNATION,
  LB_TBL_COL_DEPARTMENT,
  LB_TBL_COL_EMPLOYEE,
  LB_TBL_COL_ACTION
} = LB_KPI_TABLE_COLS;

let columnsT = '';

const ListWithTooltip = ({ items, type }) => {
  const limit = 2; // Number of items to display initially
  const displayedItems = items?.slice(0, limit) || [];
  const hiddenItems = items?.slice(limit) || [];

  return (
    <div>
      {displayedItems?.length > 0 ? (
        displayedItems.map((item, idx) => (
          <div key={item._id} className={`${idx > 0 ? 'mt-3' : ''}`}>
            <span className="bg-Platinum fw-bold p-1">{item?.name || ''}</span>
          </div>
        ))
      ) : (
        <div>
          <span>-</span>
        </div>
      )}
      {hiddenItems.length > 0 && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip">{hiddenItems.map((e) => e.name).join(', ')}</Tooltip>}
        >
          <div className="mt-3">
            <span className="bg-Platinum fw-bold p-1">
              +{hiddenItems.length} more {type}
            </span>
          </div>
        </OverlayTrigger>
      )}
    </div>
  );
};

const KPITable = ({ state, setState }) => {
  const dispatch = useDispatch();
  const {
    data: leaveTypeData,
    isLoading,
    isFetching,
    refetch: kpiFetch
  } = useGetKPIQuery({
    search: state?.search || '',
    skip: skipRow(state?.currentPage, state?.rowsPerPage),
    limit: state?.rowsPerPage,
    sort: state?.sortField,
    sortType: state.sortDirection ? DESC : ASC
  });

  const [deleteKpi] = useDeleteKPIMutation();

  columnsT = [
    {
      name: LB_tBL_COL_NAME,
      sortField: SR_LEAVE_TYPE,
      selector: (row) => row?.name,
      sortable: true,
      cell: (row) => (
        <div>
          <span className="fw-bold ms-1">{row?.name || ''}</span>
        </div>
      )
    },
    {
      name: LB_TBL_COL_DESIGNATION,
      selector: (row) => row?.designation,
      sortable: false,
      cell: (row) => <ListWithTooltip items={row?.designation} type="designation" />
    },
    {
      name: LB_TBL_COL_DEPARTMENT,
      selector: (row) => row?.department,
      sortable: false,
      cell: (row) => <ListWithTooltip items={row?.department} type="department" />
    },
    {
      name: LB_TBL_COL_EMPLOYEE,
      selector: (row) => row?.employee,
      sortable: false,
      cell: (row) => <ListWithTooltip items={row?.employee} type="employees" />
    },
    {
      name: LB_TBL_COL_ACTION,
      selector: () => {},
      sortable: false,
      cell: (row) => (
        <RowActionMenu
          actions={[
            {
              label: 'Edit',
              icon: 'icofont-edit',
              variant: 'success',
              onClick: () => {
                handleSetEditData(row);
              }
            },
            {
              label: 'Delete',
              icon: 'icofont-ui-delete',
              variant: 'danger',
              onClick: () => {
                setState({ ...state, isDelete: { id: row?._id, status: true } });
              }
            }
          ]}
        />
      )
    }
  ];

  useEffect(() => {
    kpiFetch();
  }, []);

  const handleSetEditData = async (row) => {
    setState((prevState) => ({
      ...prevState,
      isModal: { model: true, submit: false },
      editKPIData: row,
      modalHeader: {
        title: LB_EDIT_KPI,
        isEdit: true
      }
    }));

    if (row?._id) {
      handleEditKPI(row);
    }
  };

  const handleEditKPI = async (row) => {
    dispatch(selectedKpi(row));
  };

  const debouncedOnSearchData = _debounce((data) => {
    setState({ ...state, search: data });
  }, 300);

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setState({ ...state, rowsPerPage: newRowsPerPage, currentPage: 1 });
  };

  const handleChangePage = (page) => {
    setState({ ...state, currentPage: page });
  };

  const handleDeleteKPI = (id) => {
    try {
      deleteKpi(id).then((res) => {
        if (res?.data?.success) {
          kpiFetch();
          setState({ ...state, isDelete: { id: '', status: false } });
        }
      });
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const handleChangeSort = (sortField) => {
    setState({ ...state, sortDirection: !state.sortDirection, sortField: sortField.sortField });
  };

  return (
    <div className="row clearfix g-3">
      <div className="col-sm-12">
        <TableCard>
          <TableToolbar
            onSearch={debouncedOnSearchData}
            actions={
              <button
                type="button"
                className="app-table-toolbar__add-btn"
                onClick={() => {
                  setState({
                    ...state,
                    selectGender: [],
                    selectDepartMentData: [],
                    selectMaritalStatus: [],
                    selectDesignationData: [],
                    selectEmployeeData: [],
                    isModal: { model: true, submit: false },
                    editLeaveTypeData: {},
                    modalHeader: {
                      title: LB_KPI_TITLE,
                      isEdit: false,
                      editId: ''
                    }
                  });
                }}
              >
                <i className="icofont-plus-circle"></i>
                {BTN_ADD_NEW_KPI}
              </button>
            }
          />
          <div className="card-body">
            <DataTable
              customStyles={sharedTableStyles}
              title={DepartmentsData.title}
              columns={columnsT}
              data={leaveTypeData?.data || []}
              defaultSortField="title"
              pagination
              paginationServer
              onSort={handleChangeSort}
              sortField={state.sortField}
              sortDirection={state.sortDirection}
              paginationTotalRows={paginationRow(
                // state.isNext,
                leaveTypeData?.isNext,
                state.rowsPerPage,
                state.currentPage,
                leaveTypeData?.data?.length
              )}
              paginationPerPage={state.rowsPerPage}
              paginationRowsPerPageOptions={[5, 10, 20, 30]}
              paginationComponentOptions={{
                noRowsPerPage: false,
                rangeSeparatorText: 'of',
                selectAllRowsItem: state.isNext,
                selectAllRowsItemText: 'All',
                paginationButtonsDisabled: state.isNext
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              selectableRows={false}
              className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
              highlightOnHover={true}
              noDataComponent={<EmptyData />}
              progressPending={isLoading || isFetching ? true : false}
              progressComponent={<TableSkeleton columns={5} />}
            />
          </div>
        </TableCard>
        <DeleteModel
          isOpen={() => {
            setState({ ...state, isDelete: { id: '', status: false } });
          }}
          open={state?.isDelete?.status}
          title={LB_DELETE_KPI}
          message={LB_DELETE_KPI_MESSAGE}
          isDelete={() => {
            handleDeleteKPI(state?.isDelete.id);
          }}
        />
      </div>
    </div>
  );
};

export default KPITable;
